import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Norrköping?", a: "Ansökan görs till Socialkontoret / tillståndsenheten i Norrköpings kommun. Verksamhet i annan kommun i Östergötlands län (t.ex. Söderköping, Finspång, Linköping, Valdemarsvik) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Norrköping?", a: "Norrköping kommun tar ut en ansökningsavgift och en årlig tillsynsavgift. Avgifterna varierar beroende på tillståndstyp och alkoholförsäljningens storlek – kontrollera aktuella avgifter på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Norrköping?", a: "Norrköping kommun anger normalt 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-norrkoping")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Norrköping" },
      ]}
      title="Serveringstillstånd Norrköping – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Norrköping: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.norrkoping, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Norrköpings kommun – Samhällsbyggnadskontoret, Miljö och hälsa",
        address: "Förvaltningshuset Rosen, Trädgårdsgatan 21, 601 81 Norrköping",
        phone: "011-15 00 00 (kommunens växel)",
        waitTime: "ca 8 veckor för stadigvarande tillstånd, ca 6 veckor för tillfälligt tillstånd till allmänheten (enligt kommunens egna riktvärden)",
        feeExamples: [
          "Ansöknings- och tillsynsavgifter enligt fastställd taxa – se kommunens sida för aktuella belopp",
          "Avgiften varierar med tillståndstyp och alkoholomsättning",
        ],
        checklist: [
          "Registreringsbevis",
          "Finansieringsplan och kontoutdrag",
          "Hyresavtal eller nyttjanderätt till lokalen",
          "Ritning över serveringsyta",
          "Brandskyddsdokumentation",
          "PBI-uppgifter och godkänt kunskapsprov",
        ],
        officialUrl: KOMMUN_LINKS.norrkoping,
      }}
      sections={[
        { h2: "Var ansöker du i Norrköping?", body: "Socialkontoret / tillståndsenheten i Norrköpings kommun ansvarar för serveringstillstånd i Norrköping kommun. För verksamhet i kringliggande kommuner som Söderköping, Finspång, Linköping, Valdemarsvik ansöker du hos respektive kommun. Varje kommun har egna blanketter och e-tjänster, men kraven enligt alkohollagen är nationella och gäller överallt." },
        { h2: "Krav från Norrköping kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) kan kommunen ha lokala riktlinjer kring serveringstider, uteservering och ordning. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt. Norrköping har särskilda regler för uteserveringar i industrilandskapet och längs Motala ström." },
        { h2: "Kunskapsprovet i Norrköping", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Det är ett skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning och nykterhet, marknadsföring m.m. Frågeantalet varierar beroende på tillståndstyp och kommun – Folkhälsomyndighetens föreskrifter FoHMFS 2014:7 anger områden och miniminivåer. Med Alkoholprovet kan du träna i samma format hemifrån och gå in på provet förberedd." },
        { h2: "Handläggningstid i Norrköping", body: "Norrköpings kommun har beslutat om maximala handläggningstider: en ansökan om stadigvarande serveringstillstånd till allmänheten, slutet sällskap, pausservering, catering eller provsmakning ska handläggas inom ca åtta veckor, medan ett tillfälligt tillstånd till allmänheten ska klaras av inom ca sex veckor, räknat från komplett ansökan. Ändringsanmälningar går normalt snabbare, tre till fyra veckor. Ansökan skickas till Samhällsbyggnadskontoret, Miljö och hälsa, som har besöksadress Förvaltningshuset Rosen, Trädgårdsgatan 21." },
        { h2: "Tillsyn och uppföljning", body: "Norrköping kommun bedriver aktiv tillsyn på serveringsställen tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd. Goda kunskaper i alkohollagen minskar risken för misstag i den dagliga driften." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-stockholm", label: "Serveringstillstånd Stockholm" },
          { to: "/serveringstillstand-goteborg", label: "Serveringstillstånd Göteborg" },
          { to: "/serveringstillstand-malmo", label: "Serveringstillstånd Malmö" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd i Norrköping – avgifter & handläggning" },
      { name: "description", content: "Allt om hur du söker serveringstillstånd i Norrköping: krav, kostnader och förberedelse inför provet i alkohollagen." },
      { property: "og:title", content: "Serveringstillstånd – Norrköping" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Norrköping." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-norrkoping" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-norrkoping" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Norrköping", url: "https://alkoholprovet.se/serveringstillstand-norrkoping" },
      ]),
    ],
  }),
});
